import { Box, Group, Text } from '@mantine/core'
import { useModals } from '@mantine/modals'
import { IconLock } from '@tabler/icons-react'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { RouterPath } from '@/router/paths'
import { FeatureKey } from '@/types/subscription'

type GetMessageContextFn = (context?: LimitContext) => {
  limit?: number
  unit?: string
}

type TranslationConfig = Partial<
  Record<FeatureKey, { id: string; getMessageContext: GetMessageContextFn }>
>

const TRANSLATION_KEYS: TranslationConfig = {
  number_of_apps: {
    id: 'subscriptions.usage.limitReached.apps',
    getMessageContext: (context?: LimitContext) => ({
      limit: context?.maxApps
    })
  },
  number_of_users: {
    id: 'subscriptions.usage.limitReached.users',
    getMessageContext: (context?: LimitContext) => ({
      limit: context?.maxUsers
    })
  },
  model_trainings: {
    id: 'subscriptions.usage.limitReached.modelTrainings',
    getMessageContext: (context?: LimitContext) => ({
      limit: context?.maxTrainings
    })
  },
  model_test_data: {
    id: 'subscriptions.usage.limitReached.modelTestData',
    getMessageContext: (context?: LimitContext) => ({
      limit: context?.maxModelTestData?.[0],
      unit: context?.maxModelTestData?.[1]
    })
  },
  user_ml_models: {
    id: 'subscriptions.usage.limitReached.userModels',
    getMessageContext: (context?: LimitContext) => ({
      limit: context?.maxUserModels
    })
  },
  dataset_storage: {
    id: 'subscriptions.usage.limitReached.datasetStorage',
    getMessageContext: (context?: LimitContext) => ({
      limit: context?.maxDatasetStorage?.[0],
      unit: context?.maxDatasetStorage?.[1]
    })
  }
}

type LimitContext = {
  maxApps?: number
  maxUsers?: number
  maxTrainings?: number
  maxUserModels?: number
  maxModelTestData?: [number, string] // limit + unit
  maxDatasetStorage?: [number, string] // limit + unit
}

export const useUsageLimitModal = () => {
  const intl = useIntl()
  const { openConfirmModal } = useModals()
  const navigate = useNavigate()

  const openUsageLimitModal = useCallback(
    (feature: FeatureKey, context?: LimitContext) => {
      openConfirmModal({
        size: 'lg',
        title: (
          <Group wrap="nowrap" align="center" gap="xs">
            <IconLock size={24} color="var(--mantine-color-yellow-8)" />
            <FormattedMessage id="subscriptions.usage.limitReached.title" />
          </Group>
        ),
        children: TRANSLATION_KEYS[feature] && (
          <Box mb="xl">
            <Text size="sm">
              <FormattedMessage
                id={TRANSLATION_KEYS[feature].id}
                values={TRANSLATION_KEYS[feature].getMessageContext(context)}
              />
            </Text>
          </Box>
        ),
        labels: {
          cancel: intl.formatMessage({
            id: 'cancel'
          }),
          confirm: intl.formatMessage({
            id: 'subscriptions.upgrade'
          })
        },
        onConfirm: () => {
          void navigate({
            pathname: RouterPath.subscription,
            search: '?openUpgrade=true'
          })
        }
      })
    },
    [openConfirmModal, intl, navigate]
  )

  return {
    openUsageLimitModal
  }
}
